/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState } from 'react';
import { useFormulaListStore } from '../../stores/formulaListStore';

export function Pagination() {
  const { page, pageSize, totalCount, setPage, setPageSize, isLoading } = useFormulaListStore();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const [jumpInput, setJumpInput] = useState('');

  function handleJump() {
    const target = parseInt(jumpInput, 10);
    if (!isNaN(target) && target >= 1 && target <= totalPages) {
      setPage(target);
    }
    setJumpInput('');
  }

  return (
    <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
      <div>
        Showing {Math.min((page - 1) * pageSize + 1, totalCount)}–{Math.min(page * pageSize, totalCount)} of {totalCount}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setPage(page - 1)}
          disabled={page <= 1 || isLoading}
          className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
        >
          ← Prev
        </button>

        <span>Page {page} of {totalPages}</span>

        <button
          onClick={() => setPage(page + 1)}
          disabled={page >= totalPages || isLoading}
          className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
        >
          Next →
        </button>

        <span className="mx-1 text-gray-300">|</span>

        <span>Go to</span>
        <input
          type="number"
          aria-label="Go to page number"
          min={1}
          max={totalPages}
          value={jumpInput}
          onChange={(e) => setJumpInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleJump(); }}
          disabled={isLoading}
          placeholder="#"
          className="w-14 border border-gray-200 rounded px-2 py-1 text-center disabled:opacity-40"
        />
        <button
          onClick={handleJump}
          disabled={isLoading || jumpInput === ''}
          className="px-2 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
        >
          Go
        </button>

        <select
          aria-label="Rows per page"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value) as 25 | 50 | 100)}
          className="ml-4 border border-gray-200 rounded px-2 py-1"
        >
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
      </div>
    </div>
  );
}
